/**
 * Mock catalog for assessment “Add questions” focus flow —
 * Library bank, past assessments, and import staging.
 *
 * The bank is deliberately large (~1.2K rows) so the pool exercises pagination
 * and the selection mix readout at realistic scale. Curated rows come first —
 * they carry the hand-written stems the blueprint gaps and import preview rely
 * on — followed by deterministically generated rows.
 */

import { MOCK_FACULTY } from "@/lib/mock/exam-data"
import { NAV_USER } from "@/lib/mock/navigation"
import type { QuestionRevision } from "@/lib/question-preview"
import type { QuestionBankStatus } from "@/lib/question-bank-status"

export type { QuestionBankStatus }
export {
  QUESTION_BANK_STATUSES,
  QUESTION_BANK_STATUS_ICON,
  QUESTION_BANK_STATUS_TONE,
  normalizeQuestionBankStatus,
} from "@/lib/question-bank-status"

export type AddQuestionSource = "library" | "past" | "import" | "create"

/** Revised Bloom's taxonomy, ordered lowest to highest cognitive demand. */
export type BloomLevel =
  "Remember" | "Understand" | "Apply" | "Analyze" | "Evaluate" | "Create"

export const BLOOM_LEVELS: readonly BloomLevel[] = [
  "Remember",
  "Understand",
  "Apply",
  "Analyze",
  "Evaluate",
  "Create",
]

/**
 * One glyph per level, so a Bloom's row is recognisable before it is read.
 *
 * Each is the verb, not a rank: recalling is a brain, understanding is the
 * lightbulb going on, applying runs the machinery, analysing takes the data
 * apart, evaluating weighs it, creating builds something new. The ladder itself
 * is carried by row order and by `BLOOM_DEMAND`, because six icons cannot say
 * "higher than" (P10, and §8.6 Case A — every one of these sits beside its
 * label).
 */
export const BLOOM_ICON: Record<BloomLevel, string> = {
  Remember: "fa-brain",
  Understand: "fa-lightbulb",
  Apply: "fa-gears",
  Analyze: "fa-magnifying-glass-chart",
  Evaluate: "fa-scale-balanced",
  Create: "fa-pen-ruler",
}

/** Questions the bank never had tagged for Bloom's. */
export const BLOOM_UNTAGGED_ICON = "fa-circle-dashed"

/** Bank row before an author is stamped on it (see `withAuthor`). */
interface SeedQuestion {
  id: string
  /**
   * Short name authors scan the bank by — the stem is the question a student
   * reads, this is what a colleague calls it in conversation.
   */
  title: string
  stem: string
  type: string
  topic: string
  difficulty: "Easy" | "Medium" | "Hard"
  /**
   * `false` when the source row carries no difficulty tag and `difficulty`
   * above is only a value the grid can sort and draw bars for.
   *
   * Bank rows are always tagged, so this is absent on them and absence means
   * tagged. Rows adapted from a paper roster or the library may not be, and a
   * panel that reads the fallback as a claim tells the author their untagged
   * question was tagged Medium.
   */
  difficultyTagged?: boolean
  sourceKind: "library" | "past" | "import"
  sourceLabel: string
  folderId: string
  /**
   * All filing locations. When absent, the question lives only in `folderId`.
   * Composer multi-select writes both: `folderId` is the primary (first) location.
   */
  folderIds?: string[]
  pbi: string
  points?: number
  /** Absent on questions the bank has never had tagged — a real gap authors care about. */
  bloom?: BloomLevel
  /**
   * Publish state. Ready can sit on a live paper; Draft is still being written.
   * Same vocabulary as assessment structure (`QuestionRecord.status`).
   * Optional on seeds — absence means Ready once stamped for the pool.
   */
  status?: QuestionBankStatus
  /**
   * When an imported row landed in the pool (ms). Used to sort Imported
   * newest-first and to show the Imported date column.
   */
  importedAt?: number
  /**
   * Extraction triage flags at the moment the row entered the pool. Direct
   * import stamps drafts without a review pass, so Duplicate / Needs attention
   * have to travel with the row — live import-store state is already settled.
   */
  importSignals?: {
    duplicate: boolean
    needsAttention: boolean
  }
  /**
   * Session-new highlight for DataTable (`bg-dt-new-row-bg` + blue dot).
   * Standing bank rows omit this.
   */
  isNew?: boolean
}

/** Filing locations for a bank row — multi-select or the legacy single `folderId`. */
export function bankQuestionFolderIds(row: {
  folderId: string
  folderIds?: string[]
}): string[] {
  return row.folderIds?.length ? row.folderIds : [row.folderId]
}

export interface AssessmentBankQuestion
  extends SeedQuestion, Record<string, unknown> {
  /**
   * Who wrote it. Authors filter the bank by colleague constantly.
   *
   * Optional because not every surface knows. A paper roster row records the
   * question but not who wrote it, and a panel that prints `Unknown` beside a
   * generated avatar has dressed a gap up as a person. Absent means absent, and
   * the surfaces that show a creator omit the line instead.
   */
  author?: string
  authorEmail?: string
  /**
   * Free-text labels the author attached in the composer. The bank's own filing
   * is topic and folder; these are whatever else the author needed to find it
   * by later, so nothing here is a taxonomy the product controls.
   */
  tags?: string[]
  /**
   * Reuse history, when the surface that built the row has a real one. The
   * bank's own rows do not: their counts are derived in `lib/question-reuse`
   * so the same item reports the same history wherever it is opened. Rows
   * adapted from the library or from a paper carry the recorded numbers here
   * instead, because those surfaces do know.
   */
  timesUsed?: number
  usedInSameCourse?: boolean
  /** Set by adapters for items recorded as never having been sat. */
  neverUsed?: boolean
  /**
   * Revisions written in the app, oldest first. Absent on every seeded row:
   * their history is derived in `lib/question-preview`. A surface that lets an
   * author edit a question appends here, and the newest entry becomes the
   * version the preview serves.
   */
  revisions?: QuestionRevision[]
  /** Ready for a paper, or still a Draft. Always set on bank / pool rows. */
  status: QuestionBankStatus
}

/**
 * Identity for `useRecordStepper`. Module scope so the hook keeps its memo
 * across renders rather than recomputing on every keystroke in the hub.
 */
export const bankQuestionId = (row: AssessmentBankQuestion) => row.id

/**
 * Question authors come from the faculty directory rather than a second
 * hand-written roster, so the person cell shows the same people the rest of the
 * app does.
 *
 * The signed-in user is in the rotation because faculty write questions: a
 * bank where the only author who has never contributed is the person looking at
 * it would make `My questions` a permanently empty room.
 */
const BANK_AUTHORS = [
  ...MOCK_FACULTY.filter((member) => member.status === "Active").map(
    (member) => ({ name: member.name, email: member.email })
  ),
  { name: NAV_USER.name, email: NAV_USER.email },
]

/**
 * Curated + section seeds the signed-in author owns. Explicit so Add questions
 * and the paper roster can demo owner Save vs non-owner Save-as-new without
 * depending on rotation index.
 */
const OWN_QUESTION_IDS = new Set([
  "bank-q2", // angina — Add questions edit / revise tests
  "bank-q4",
  "bank-q5",
  "bank-q7",
  "q-bp-cardio-analyze-1",
  "q-bp-cardio-apply-1",
  "q-bp-cardio-apply-2",
])

/** Someone else's items — non-owner may only mint a copy. */
const OTHER_QUESTION_IDS = new Set([
  "bank-q1",
  "bank-q3",
  "bank-q3-fib",
  "bank-q3-hotspot",
  "bank-q3b",
  "bank-q3c",
  "bank-q3d",
  "bank-q3e",
  "bank-q4b",
  "bank-q4c",
  "bank-q6",
  "q-bp-neuro-understand-1",
  "q-bp-neuro-understand-2",
  "q-bp-pharm-remember-1",
  "q-bp-cardio-apply-3",
])

const NAV_AUTHOR = { name: NAV_USER.name, email: NAV_USER.email }

/** First active faculty — never the signed-in user. */
const OTHER_AUTHOR = BANK_AUTHORS.find(
  (member) => member.email !== NAV_USER.email
) ?? BANK_AUTHORS[0]

/** Deterministic so reloads, snapshots, and the pool order all match. */
function withAuthor(row: SeedQuestion, index: number): AssessmentBankQuestion {
  const author = OWN_QUESTION_IDS.has(row.id)
    ? NAV_AUTHOR
    : OTHER_QUESTION_IDS.has(row.id)
      ? OTHER_AUTHOR
      : BANK_AUTHORS[index % BANK_AUTHORS.length]!
  return {
    ...row,
    author: author.name,
    authorEmail: author.email,
    status: row.status ?? "Ready",
  }
}

/**
 * An author for a surface that stores the question but not who wrote it.
 *
 * Mock attribution, same as the rest of this file: the bank stamps faculty on
 * every row it generates, and a paper roster row is the one place that arrives
 * without one. Rather than leave those questions anonymous while every other
 * surface names a person, they draw from the same directory, keyed on the id so
 * a given question keeps its author across reloads.
 *
 * Replace with the real recorded author when the roster carries one.
 */
export function bankAuthorForId(id: string): { name: string; email: string } {
  let hash = 0
  for (let index = 0; index < id.length; index += 1) {
    hash = (hash * 31 + id.charCodeAt(index)) >>> 0
  }
  return BANK_AUTHORS[hash % BANK_AUTHORS.length]
}

/** Ordinal position for `SignalBarsCell` — difficulty is a scale, not a status. */
export const DIFFICULTY_LEVEL: Record<
  AssessmentBankQuestion["difficulty"],
  number
> = {
  Easy: 1,
  Medium: 2,
  Hard: 3,
}

/** Paired with the bar count so the cell still reads without colour. */
export const DIFFICULTY_TONE: Record<
  AssessmentBankQuestion["difficulty"],
  "success" | "warning" | "danger"
> = {
  Easy: "success",
  Medium: "warning",
  Hard: "danger",
}

export const SOURCE_ICON: Record<AssessmentBankQuestion["sourceKind"], string> =
  {
    library: "fa-books",
    past: "fa-clock-rotate-left",
    import: "fa-file-import",
  }

/** Deterministic 0..1 from any string, so a reload never reshuffles a stat. */
export function hashUnit(seed: string): number {
  let hash = 2166136261
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return ((hash >>> 0) % 1000) / 1000
}

/**
 * The upper-lower discrimination index D: the pass rate of the top third of a
 * cohort minus the pass rate of the bottom third.
 *
 * Derived from PBI rather than seeded separately, because the two measure the
 * same property — whether the item separates strong examinees from weak ones —
 * with different arithmetic. In real data they track each other closely and
 * disagree near the cut points, which is exactly why an author is offered both,
 * so the derivation keeps the correlation and adds a small deterministic spread
 * instead of inventing an unrelated number.
 *
 * `null` means the item has never been sat, which is a different statement from
 * a low score.
 */
export function discriminationIndex(
  row: Pick<AssessmentBankQuestion, "id" | "pbi">
): number | null {
  const pbi = Number(row.pbi)
  if (Number.isNaN(pbi)) return null
  const spread = (hashUnit(`${row.id}:d`) - 0.5) * 0.16
  return Math.min(0.95, Math.max(0, Number((pbi + spread).toFixed(2))))
}

export interface AssessmentBlueprintGap {
  topic: string
  need: string
  severity: "high" | "medium"
  targetCount: number
  difficulty: AssessmentBankQuestion["difficulty"]
  questionType: string
  /**
   * Cognitive demand the plan asks for. A blueprint that names only topic and
   * type says nothing about whether the paper tests recall or judgement, which
   * is the one thing an accreditation review asks about — so coverage groups on
   * it, and `matchesGap` counts a question against a target only when the level
   * agrees.
   */
  bloom: BloomLevel
}

/**
 * Bank folders are a real two-level tree (subject > sub-folder), mirroring the
 * question bank. `topic` stays the subject name so blueprint gaps keep matching
 * on it while folders carry the finer grain.
 */
export interface AssessmentBankFolder {
  id: string
  name: string
  parentId: string | null
}

/**
 * A document already attached to this section, offered as something to read
 * questions out of.
 *
 * The size and page count are here because the import picker shows them, and
 * it shows them because reading a document costs the author two to five
 * minutes. "Cardiology reading pack" and "Week 4 lecture slides" are equally
 * plausible until you know one is 38 pages and the other is 12, which is the
 * fact that decides which one to spend the wait on.
 */
export interface AssessmentReference {
  id: string
  name: string
  /** Uppercase extension, shown as-is. Derived at authoring time, not parsed. */
  kind: "PDF" | "DOCX" | "DOC" | "CSV" | "XLSX"
  /** Already formatted, since nothing computes against it. */
  size: string
  /** Absent for a spreadsheet, where a page count would be a fiction. */
  pages?: number
}

export interface AssessmentAddContext {
  courseCode: string
  courseName: string
  assessmentTitle: string
  sectionTitle: string
  /**
   * Root folders this course teaches from. The bank is program-wide — twelve
   * subjects, ~1.2K questions — but a CS-101 midterm will never draw on
   * Musculoskeletal, so the pool leads with these and keeps the rest behind an
   * explicit "search the whole bank".
   */
  courseFolderIds: string[]
  references: AssessmentReference[]
  blueprintGaps: AssessmentBlueprintGap[]
  pastAssessments: { id: string; name: string; year: string }[]
}

/**
 * Ids are prefixed `bank-` on purpose. Section rosters (`SECTION_SEED`) number
 * their questions `q1`, `q2`, … to match the assessment structure page, and
 * membership is keyed by id: an unprefixed `q2` here made an unrelated
 * Cardiology question read as already on the section.
 */
const CURATED_QUESTIONS: SeedQuestion[] = [
  {
    id: "bank-q1",
    title: "Heart failure pathophysiology",
    stem: "Describe the pathophysiology of heart failure.",
    type: "Multiple Choice",
    points: 1,
    topic: "Cardiology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.35",
    bloom: "Understand",
  },
  {
    id: "bank-q2",
    title: "Angina symptoms",
    stem: "Which of the following is a symptom of angina?",
    type: "Multiple Choice",
    points: 1,
    topic: "Cardiology",
    difficulty: "Easy",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.45",
    bloom: "Remember",
  },
  {
    id: "bank-q3",
    title: "Cardiac drug mechanisms",
    stem: "Match the medications to their mechanisms of action.",
    type: "Match the following",
    points: 1,
    topic: "Cardiology",
    difficulty: "Hard",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.22",
    bloom: "Analyze",
  },
  {
    id: "bank-q3-fib",
    title: "Facial nerve blank",
    stem: "The _________ nerve provides motor innervation to the muscles of facial expression.",
    type: "Fill in the blank",
    points: 1,
    topic: "Neurology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Neurology",
    folderId: "f2",
    pbi: "0.38",
    bloom: "Remember",
  },
  {
    id: "bank-q3-hotspot",
    title: "SA node on conduction diagram",
    stem: "Select the region on the conduction system diagram that initiates the cardiac impulse.",
    type: "Hotspot",
    points: 1,
    topic: "Cardiology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.41",
    bloom: "Apply",
    status: "Draft",
  },
  {
    id: "bank-q3b",
    title: "SA node function",
    stem: "What is the primary function of the SA node?",
    type: "Multiple Choice",
    points: 1,
    topic: "Cardiology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.40",
    bloom: "Remember",
  },
  {
    id: "bank-q3c",
    title: "Leading cause of MI",
    stem: "Identify the most common cause of myocardial infarction.",
    type: "Multiple Choice",
    points: 1,
    topic: "Cardiology",
    difficulty: "Easy",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.50",
    bloom: "Remember",
  },
  {
    id: "bank-q3d",
    title: "STEMI vs NSTEMI",
    stem: "Explain the difference between STEMI and NSTEMI.",
    type: "Essay",
    points: 5,
    topic: "Cardiology",
    difficulty: "Hard",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.20",
    bloom: "Analyze",
  },
  {
    id: "bank-q3e",
    title: "ECG signs of acute STEMI",
    stem: "Which ECG finding most strongly indicates an acute STEMI?",
    type: "Multiple Choice",
    points: 1,
    topic: "Cardiology",
    difficulty: "Hard",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.24",
    bloom: "Apply",
  },
  {
    id: "bank-q4",
    title: "Early Parkinson's signs",
    stem: "What are the early signs of Parkinson's disease?",
    type: "Multiple Choice",
    points: 1,
    topic: "Neurology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Neurology",
    folderId: "f2",
    pbi: "0.50",
    bloom: "Remember",
  },
  {
    id: "bank-q4b",
    title: "Frontal lobe function",
    stem: "Describe the function of the frontal lobe.",
    type: "Multiple Choice",
    points: 1,
    topic: "Neurology",
    difficulty: "Hard",
    sourceKind: "library",
    sourceLabel: "Neurology",
    folderId: "f2",
    pbi: "0.30",
    bloom: "Understand",
  },
  {
    id: "bank-q4c",
    title: "Demyelination and conduction",
    stem: "Explain how demyelination changes neural signal conduction.",
    type: "Essay",
    points: 5,
    topic: "Neurology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Neurology",
    folderId: "f2",
    pbi: "0.38",
    bloom: "Understand",
  },
  {
    id: "q_path1",
    title: "Apoptosis definition",
    stem: "Define cellular apoptosis.",
    type: "Multiple Choice",
    points: 1,
    topic: "Pathology",
    difficulty: "Easy",
    sourceKind: "library",
    sourceLabel: "Pathology",
    folderId: "f3",
    pbi: "0.60",
    bloom: "Remember",
  },
  {
    id: "q_path2",
    title: "Stages of necrosis",
    stem: "What are the stages of necrosis?",
    type: "Multiple Choice",
    points: 1,
    topic: "Pathology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Pathology",
    folderId: "f3",
    pbi: "0.40",
    bloom: "Remember",
  },
  {
    id: "q_pharm1",
    title: "Aspirin mechanism",
    stem: "Mechanism of action of Aspirin?",
    type: "Multiple Choice",
    points: 1,
    topic: "Pharmacology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Pharmacology",
    folderId: "f4",
    pbi: "0.55",
    bloom: "Understand",
  },
  {
    id: "q_pharm2",
    title: "Fastest route of administration",
    stem: "Which route of administration produces the fastest systemic effect?",
    type: "Multiple Choice",
    points: 1,
    topic: "Pharmacology",
    difficulty: "Easy",
    sourceKind: "library",
    sourceLabel: "Pharmacology",
    folderId: "f4",
    pbi: "0.61",
    bloom: "Remember",
  },
  {
    id: "bank-q5",
    title: "Mitochondrial function",
    stem: "What is the primary function of the mitochondria?",
    type: "Multiple Choice",
    points: 1,
    topic: "Cell Biology",
    difficulty: "Easy",
    sourceKind: "past",
    sourceLabel: "CS-101 Midterm 2024",
    folderId: "pa1",
    pbi: "0.80",
    bloom: "Remember",
  },
  {
    id: "bank-q6",
    title: "Cellular respiration",
    stem: "Explain the process of cellular respiration.",
    type: "Essay",
    points: 5,
    topic: "Cell Biology",
    difficulty: "Hard",
    sourceKind: "past",
    sourceLabel: "CS-101 Midterm 2024",
    folderId: "pa1",
    pbi: "0.40",
    bloom: "Understand",
  },
  {
    id: "bank-q7",
    title: "Early sepsis warning signs",
    stem: "List two early warning signs of sepsis.",
    type: "Short Answer",
    points: 2,
    topic: "Clinical",
    difficulty: "Medium",
    sourceKind: "past",
    sourceLabel: "CS-101 Pop Quiz 1",
    folderId: "pa2",
    pbi: "0.55",
    bloom: "Remember",
  },
  {
    // Unsat library item — preview shows em dashes for item analysis. Not an
    // import: Imported starts empty until the author reads a document.
    id: "bank-q-unsat",
    title: "Capital of France",
    stem: "What is the capital city of France?",
    type: "Multiple Choice",
    points: 1,
    topic: "General",
    difficulty: "Easy",
    sourceKind: "library",
    sourceLabel: "General",
    folderId: "f1",
    pbi: "--",
    bloom: "Remember",
  },
]

// ─── Generated bank ──────────────────────────────────────────────────────────

interface TopicSeedChild {
  id: string
  name: string
  subjects: string[]
}

interface TopicSeed {
  topic: string
  folderId: string
  children: TopicSeedChild[]
}

/**
 * Subject folders with real sub-folders, the way the question bank is organised.
 * `topic` is the subject name so blueprint gaps keep matching on it.
 */
const TOPIC_SEEDS: TopicSeed[] = [
  {
    topic: "Cardiology",
    folderId: "f1",
    children: [
      {
        id: "f1-a",
        name: "Arrhythmias & Conduction",
        subjects: ["atrial fibrillation", "AV nodal re-entry"],
      },
      {
        id: "f1-b",
        name: "Valvular Disease",
        subjects: ["aortic stenosis", "mitral regurgitation"],
      },
      {
        id: "f1-c",
        name: "Heart Failure & Output",
        subjects: ["cardiac output", "beta blockade"],
      },
    ],
  },
  {
    topic: "Neurology",
    folderId: "f2",
    children: [
      {
        id: "f2-a",
        name: "Movement & Coordination",
        subjects: ["cerebellar ataxia", "basal ganglia output"],
      },
      {
        id: "f2-b",
        name: "Cortical Function",
        subjects: ["seizure thresholds", "aphasia localisation"],
      },
      {
        id: "f2-c",
        name: "Cranial Nerves",
        subjects: ["cranial nerve palsy", "the blood-brain barrier"],
      },
    ],
  },
  {
    topic: "Pathology",
    folderId: "f3",
    children: [
      {
        id: "f3-a",
        name: "Cell Injury & Death",
        subjects: ["ischemic injury", "apoptotic signalling"],
      },
      {
        id: "f3-b",
        name: "Inflammation",
        subjects: ["granuloma formation", "chronic inflammation"],
      },
      {
        id: "f3-c",
        name: "Neoplasia",
        subjects: ["neoplastic transformation", "tumour grading"],
      },
    ],
  },
  {
    topic: "Pharmacology",
    folderId: "f4",
    children: [
      {
        id: "f4-a",
        name: "Antibiotics & Antimicrobials",
        subjects: ["beta-lactam selection", "aminoglycoside dosing"],
      },
      {
        id: "f4-b",
        name: "Analgesics & Pain Management",
        subjects: ["opioid receptor subtypes", "therapeutic index"],
      },
      {
        id: "f4-c",
        name: "Cardiovascular Drugs",
        subjects: ["receptor antagonism", "renal clearance"],
      },
      {
        id: "f4-d",
        name: "CNS & Psychotropics",
        subjects: [
          "first-pass metabolism",
          "antipsychotic side-effect profiles",
        ],
      },
    ],
  },
  {
    topic: "Cell Biology",
    folderId: "f5",
    children: [
      {
        id: "f5-a",
        name: "Membrane Transport",
        subjects: ["membrane transport", "ion gradients"],
      },
      {
        id: "f5-b",
        name: "Mitosis & Meiosis",
        subjects: ["the cell cycle", "chromosomal crossover"],
      },
      {
        id: "f5-c",
        name: "Molecular Biology",
        subjects: ["protein trafficking", "mitochondrial function"],
      },
    ],
  },
  {
    topic: "Clinical",
    folderId: "f6",
    children: [
      {
        id: "f6-a",
        name: "Sepsis & Resuscitation",
        subjects: ["sepsis recognition", "fluid resuscitation"],
      },
      {
        id: "f6-b",
        name: "Communication & Handoff",
        subjects: ["handoff communication", "informed consent"],
      },
      {
        id: "f6-c",
        name: "Bedside Assessment",
        subjects: ["pain assessment", "vital sign interpretation"],
      },
    ],
  },
  {
    topic: "Immunology",
    folderId: "f7",
    children: [
      {
        id: "f7-a",
        name: "Innate Immunity",
        subjects: ["complement activation", "phagocyte recruitment"],
      },
      {
        id: "f7-b",
        name: "Adaptive Immunity",
        subjects: ["antigen presentation", "vaccine response"],
      },
      {
        id: "f7-c",
        name: "Hypersensitivity",
        subjects: ["hypersensitivity", "autoimmune tolerance"],
      },
    ],
  },
  {
    topic: "Microbiology",
    folderId: "f8",
    children: [
      {
        id: "f8-a",
        name: "Bacteriology",
        subjects: ["gram-negative sepsis", "biofilm formation"],
      },
      {
        id: "f8-b",
        name: "Virology",
        subjects: ["viral replication", "latency and reactivation"],
      },
      {
        id: "f8-c",
        name: "Antimicrobial Resistance",
        subjects: ["antibiotic resistance", "efflux pump mechanisms"],
      },
    ],
  },
  {
    topic: "Endocrinology",
    folderId: "f9",
    children: [
      {
        id: "f9-a",
        name: "Glucose Regulation",
        subjects: ["insulin resistance", "counter-regulatory hormones"],
      },
      {
        id: "f9-b",
        name: "Thyroid & Parathyroid",
        subjects: ["thyroid feedback", "calcium regulation"],
      },
      {
        id: "f9-c",
        name: "Adrenal Axis",
        subjects: ["cortisol rhythm", "mineralocorticoid excess"],
      },
    ],
  },
  {
    topic: "Respiratory",
    folderId: "f10",
    children: [
      {
        id: "f10-a",
        name: "Gas Exchange",
        subjects: ["ventilation-perfusion mismatch", "gas exchange"],
      },
      {
        id: "f10-b",
        name: "Obstructive Disease",
        subjects: ["airway remodeling", "bronchodilator response"],
      },
      {
        id: "f10-c",
        name: "Pleural Disease",
        subjects: ["pleural effusion", "pneumothorax physiology"],
      },
    ],
  },
  {
    topic: "Renal",
    folderId: "f11",
    children: [
      {
        id: "f11-a",
        name: "Filtration & Clearance",
        subjects: ["glomerular filtration", "tubular reabsorption"],
      },
      {
        id: "f11-b",
        name: "Acid-Base",
        subjects: ["acid-base balance", "anion gap interpretation"],
      },
      {
        id: "f11-c",
        name: "Acute Kidney Injury",
        subjects: ["acute kidney injury", "contrast nephropathy"],
      },
    ],
  },
  {
    topic: "Musculoskeletal",
    folderId: "f12",
    children: [
      {
        id: "f12-a",
        name: "Shoulder Complex",
        subjects: ["joint instability", "rotator cuff loading"],
      },
      {
        id: "f12-b",
        name: "Cervical Spine",
        subjects: ["dermatomal mapping", "cervical radiculopathy"],
      },
      {
        id: "f12-c",
        name: "Lumbar Spine & Gait",
        subjects: ["gait mechanics", "muscle fatigue", "fracture healing"],
      },
    ],
  },
]

/**
 * Folders with nothing filed in them yet. Programs build the shelf before they
 * write the questions — a new subject for next term, a sub-folder opened the day
 * a standard changed — so "this folder is empty" is a normal state, not a
 * filtered-out one, and the flow has to tell them apart. No generated row
 * references these ids, so they stay empty however the bank grows.
 */
const EMPTY_FOLDER_SEEDS: AssessmentBankFolder[] = [
  // A sub-folder inside a busy subject: the parent has hundreds of questions,
  // this shelf has none.
  { id: "f2-empty", name: "Valvular Disease", parentId: "f2" },
  // A whole subject the program has only just added.
  { id: "f-reasoning", name: "Clinical Reasoning", parentId: null },
  { id: "f-reasoning-a", name: "Case Vignettes", parentId: "f-reasoning" },
]

/**
 * Root folders CS-101 teaches from. Declared here rather than only on the
 * context because the generator needs it too: a question can only turn up on a
 * past CS-101 paper if CS-101 covers the subject.
 */
const CS101_FOLDER_IDS = ["f1", "f2", "f3", "f4", "f5", "f6", "f-reasoning"]

export const ASSESSMENT_BANK_FOLDERS: AssessmentBankFolder[] = [
  ...TOPIC_SEEDS.flatMap((seed) => [
    { id: seed.folderId, name: seed.topic, parentId: null },
    ...seed.children.map((child) => ({
      id: child.id,
      name: child.name,
      parentId: seed.folderId,
    })),
  ]),
  ...EMPTY_FOLDER_SEEDS,
]

/**
 * The title says what the question asks *about* the subject, so a folder of 80
 * rows reads as a list of distinct items rather than 80 variations of one stem.
 */
const TITLE_ANGLES: Record<BloomLevel, string> = {
  Remember: "definition",
  Understand: "mechanism",
  Apply: "first-line management",
  Analyze: "differentiating findings",
  Evaluate: "plan justification",
  Create: "monitoring plan",
}

const STEM_TEMPLATES: Record<BloomLevel, (subject: string) => string> = {
  Remember: (subject) => `Which term best describes ${subject}?`,
  Understand: (subject) => `Explain how ${subject} affects normal physiology.`,
  Apply: (subject) =>
    `A patient presents with findings consistent with ${subject}. Which intervention is indicated first?`,
  Analyze: (subject) =>
    `Compare two competing explanations for ${subject} and identify the distinguishing finding.`,
  Evaluate: (subject) =>
    `Evaluate which management plan is most defensible for a patient with ${subject}, and justify the choice.`,
  Create: (subject) =>
    `Design a monitoring plan for a patient at risk of ${subject}, including escalation criteria.`,
}

/** Bloom's levels weighted by difficulty — harder questions skew to higher demand. */
const BLOOM_BY_DIFFICULTY: Record<
  AssessmentBankQuestion["difficulty"],
  BloomLevel[]
> = {
  Easy: [
    "Remember",
    "Remember",
    "Remember",
    "Understand",
    "Understand",
    "Apply",
  ],
  Medium: ["Understand", "Understand", "Apply", "Apply", "Apply", "Analyze"],
  Hard: ["Apply", "Analyze", "Analyze", "Evaluate", "Evaluate", "Create"],
}

const DIFFICULTIES: AssessmentBankQuestion["difficulty"][] = [
  "Easy",
  "Easy",
  "Medium",
  "Medium",
  "Medium",
  "Hard",
  "Hard",
]

const QUESTION_TYPES = [
  "Multiple Choice",
  "Multiple Choice",
  "Multiple Choice",
  "Multiple Choice",
  "Multiple Select",
  "True or False",
  "Short Answer",
  "Essay",
  "Fill in the blank",
  "Match the following",
  "Hotspot",
]

/**
 * The three papers with hand-authored shapes in `course-objectives.ts`, which
 * the Adapt flow reads. Their ids and names are fixed by that file.
 */
const CURATED_PAST_ASSESSMENTS = [
  { id: "pa2", name: "CS-101 Pop Quiz 1", year: "2025" },
  { id: "pa3", name: "CS-101 Final 2024", year: "2024" },
  { id: "pa1", name: "CS-101 Midterm 2024", year: "2024" },
]

/** A term's sittings, latest first, so a year reads newest to oldest. */
const ARCHIVED_PAPER_TITLES = ["Final", "Pop Quiz 2", "Midterm", "Pop Quiz 1"]

/**
 * Everything before 2024.
 *
 * A course that has run for a dozen years has fifty-odd papers behind it, and
 * the navigator has to hold up under that: three papers was a fixture that made
 * the rail look easier than the job is. They are generated rather than written
 * out because nothing about an eleven-year-old quiz is interesting except that
 * it exists and is in the way.
 */
function archivedPastAssessments() {
  const papers: { id: string; name: string; year: string }[] = []
  for (let year = 2023; year >= 2012; year -= 1) {
    for (const title of ARCHIVED_PAPER_TITLES) {
      papers.push({
        id: `pa-${year}-${title.toLowerCase().replace(/\s+/g, "-")}`,
        name: `CS-101 ${title} ${year}`,
        year: String(year),
      })
    }
  }
  return papers
}

/** Newest first, which is the order the rail and the Location tree both use. */
const PAST_ASSESSMENTS = [
  ...CURATED_PAST_ASSESSMENTS,
  ...archivedPastAssessments(),
]

const PAST_ASSESSMENT_LABELS = PAST_ASSESSMENTS.map((paper) => ({
  label: paper.name,
  folderId: paper.id,
}))

/** Linear congruential generator — deterministic so every render and reload matches. */
function makeRng(seed: number) {
  let state = seed >>> 0
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0
    return state / 4294967296
  }
}

function pick<T>(rng: () => number, items: readonly T[]): T {
  return items[Math.floor(rng() * items.length)]
}

/**
 * Which paper a past question came off, weighted hard toward the recent ones.
 *
 * A bank holds dozens of questions off last year's final and one or two off a
 * quiz from a decade ago: papers get retired, and the ones nobody reuses stop
 * accumulating. Flat odds across fifty papers would have given every year the
 * same weight and made the tail of the list look worth reading.
 */
function pickPastPaper(rng: () => number) {
  const roll = rng()
  const index = Math.floor(roll * roll * roll * PAST_ASSESSMENT_LABELS.length)
  return PAST_ASSESSMENT_LABELS[index]
}

function generateBankQuestions(perTopic: number): AssessmentBankQuestion[] {
  const rng = makeRng(20260726)
  const rows: AssessmentBankQuestion[] = []

  TOPIC_SEEDS.forEach((seed, topicIndex) => {
    for (let index = 0; index < perTopic; index += 1) {
      const difficulty = pick(rng, DIFFICULTIES)
      const bloomRoll = rng()
      const bloom =
        bloomRoll < 0.06
          ? undefined
          : pick(rng, BLOOM_BY_DIFFICULTY[difficulty])
      const child = pick(rng, seed.children)
      const subject = pick(rng, child.subjects)
      // A past CS-101 paper can only hold subjects CS-101 teaches, so subjects
      // owned by other courses never sit under "Past assessments".
      const isPast = rng() < 0.18 && CS101_FOLDER_IDS.includes(seed.folderId)
      const past = pickPastPaper(rng)
      const hasPbi = rng() > 0.22
      const author = pick(rng, BANK_AUTHORS)

      const angle = TITLE_ANGLES[bloom ?? "Understand"]

      rows.push({
        id: `gq-${topicIndex}-${index}`,
        title: `${subject[0].toUpperCase()}${subject.slice(1)}: ${angle}`,
        stem: STEM_TEMPLATES[bloom ?? "Understand"](subject),
        type: pick(rng, QUESTION_TYPES),
        topic: seed.topic,
        difficulty,
        sourceKind: isPast ? "past" : "library",
        sourceLabel: isPast ? past.label : child.name,
        folderId: isPast ? past.folderId : child.id,
        pbi: hasPbi ? (0.12 + rng() * 0.62).toFixed(2) : "--",
        points: 1 + Math.floor(rng() * 4),
        bloom,
        author: author.name,
        authorEmail: author.email,
        // A small draft share so Status is filterable without flooding the pool.
        status: rng() < 0.08 ? "Draft" : "Ready",
      })
    }
  })

  return rows
}

export const ASSESSMENT_BANK_QUESTIONS: AssessmentBankQuestion[] = [
  ...CURATED_QUESTIONS.map(withAuthor),
  ...generateBankQuestions(100),
]

/**
 * Questions already on each section when the author opens Add questions —
 * same roster as assessment structure.
 *
 * Seeds are aligned to `blueprintGaps` on purpose: Blueprint coverage is
 * assessment-wide and matches topic + Bloom + difficulty + type. A roster of
 * Cell Biology / Clinical items against Cardiology / Neurology targets made
 * every gap read 0 of N while the paper looked full — which is a lie.
 */
const SECTION_SEED: Record<string, SeedQuestion[]> = {
  A: [
    // Cardiology · Analyze · Hard MCQ — need 2, seed 1 (under)
    {
      id: "q-bp-cardio-analyze-1",
      title: "STEMI ECG progression",
      stem: "A patient presents with crushing chest pain. Which ECG finding best supports acute STEMI requiring immediate reperfusion?",
      type: "Multiple Choice",
      topic: "Cardiology",
      difficulty: "Hard",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.42",
      points: 5,
      bloom: "Analyze",
    },
    // Cardiology · Apply · Medium MCQ — need 3; A holds 3, B adds 2 more → over
    {
      id: "q-bp-cardio-apply-1",
      title: "Heart failure diuretic choice",
      stem: "Which diuretic is first-line for volume overload in acute decompensated heart failure?",
      type: "Multiple Choice",
      topic: "Cardiology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.38",
      points: 5,
      bloom: "Apply",
    },
    {
      id: "q-bp-cardio-apply-2",
      title: "AF rate control agent",
      stem: "For a hemodynamically stable patient with new atrial fibrillation and rapid ventricular response, which agent is preferred for rate control?",
      type: "Multiple Choice",
      topic: "Cardiology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.35",
      points: 5,
      bloom: "Apply",
    },
    {
      id: "q-bp-cardio-apply-3",
      title: "ACS antiplatelet loading",
      stem: "Which antiplatelet regimen is appropriate for loading before PCI in NSTE-ACS?",
      type: "Multiple Choice",
      topic: "Cardiology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.40",
      points: 5,
      bloom: "Apply",
    },
    // Neurology · Understand · Medium MCQ — need 2, seed 1 (under)
    {
      id: "q-bp-neuro-understand-1",
      title: "Stroke territory signs",
      stem: "Which clinical finding most strongly suggests an MCA territory ischemic stroke?",
      type: "Multiple Choice",
      topic: "Neurology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Neurology",
      folderId: "f2",
      pbi: "0.33",
      points: 5,
      bloom: "Understand",
    },
    // Pharmacology · Remember · Easy MCQ — need 1, seed 1 (met)
    {
      id: "q-bp-pharm-remember-1",
      title: "Beta blocker primary action",
      stem: "Beta-adrenergic antagonists primarily reduce heart rate by blocking which receptor?",
      type: "Multiple Choice",
      topic: "Pharmacology",
      difficulty: "Easy",
      sourceKind: "library",
      sourceLabel: "Pharmacology",
      folderId: "f4",
      pbi: "0.45",
      points: 5,
      bloom: "Remember",
    },
  ],
  B: [
    // Extra Cardiology Apply Medium → pushes that target over (3 in A + 2 here = 5 of 3)
    {
      id: "q-bp-cardio-apply-4",
      title: "Hypertensive urgency oral agent",
      stem: "Which oral agent is appropriate for gradual blood-pressure reduction in hypertensive urgency without end-organ damage?",
      type: "Multiple Choice",
      topic: "Cardiology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.30",
      points: 5,
      bloom: "Apply",
    },
    {
      id: "q-bp-cardio-apply-5",
      title: "Statin intensity after MI",
      stem: "After an uncomplicated MI, which statin intensity is guideline-preferred for secondary prevention?",
      type: "Multiple Choice",
      topic: "Cardiology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.36",
      points: 5,
      bloom: "Apply",
    },
    // Neurology Understand — second of two (met when combined with A)
    {
      id: "q-bp-neuro-understand-2",
      title: "Parkinson motor triad",
      stem: "Which triad best characterizes idiopathic Parkinson disease?",
      type: "Multiple Choice",
      topic: "Neurology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Neurology",
      folderId: "f2",
      pbi: "0.41",
      points: 5,
      bloom: "Understand",
    },
  ],
  C: [
    // Cardiology · Evaluate · Hard Essay — need 1 (met)
    {
      id: "q-bp-cardio-essay-1",
      title: "HFpEF pathophysiology essay",
      stem: "Discuss the pathophysiology of heart failure with preserved ejection fraction (HFpEF) and justify one evidence-based treatment approach.",
      type: "Essay",
      topic: "Cardiology",
      difficulty: "Hard",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.55",
      points: 15,
      bloom: "Evaluate",
    },
    // Pharmacology · Analyze · Medium Short Answer — need 2, seed 1 (under)
    {
      id: "q-bp-pharm-sa-1",
      title: "Warfarin interaction rationale",
      stem: "Explain why adding a CYP2C9 inhibitor increases INR in a patient on steady-dose warfarin.",
      type: "Short Answer",
      topic: "Pharmacology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Pharmacology",
      folderId: "f4",
      pbi: "0.34",
      points: 8,
      bloom: "Analyze",
    },
  ],
}

/** Roster already on a section when Add questions opens — Mix measures this. */
export function sectionQuestionsAlreadyAdded(
  sectionId: string
): AssessmentBankQuestion[] {
  const seeds = SECTION_SEED[sectionId] ?? []
  return seeds.map((row, index) => withAuthor(row, index + 40))
}

export interface AssessmentSectionSummary {
  id: string
  title: string
}

/**
 * Every section of the assessment being built, in paper order.
 *
 * Add questions is scoped to one of them, but Mix reports on all three: an
 * author judging whether section A came out too hard is really asking how it
 * compares with B and C, and that comparison is unanswerable from inside one
 * section. Structure view reads the same list, so the titles cannot drift.
 */
export const ASSESSMENT_SECTIONS: readonly AssessmentSectionSummary[] = [
  { id: "A", title: "Multiple Choice Questions" },
  { id: "B", title: "Hotspot & Fill in the Blanks" },
  { id: "C", title: "Essay & Long Answer" },
]

export const ASSESSMENT_ADD_CONTEXT: AssessmentAddContext = {
  courseCode: "CS-101",
  courseName: "Clinical Sciences I",
  assessmentTitle: "CS-101 Midterm",
  sectionTitle: "Multiple Choice Questions",
  // Clinical Sciences I covers the systems and foundations blocks; the bank's
  // Immunology, Microbiology, Endocrinology, Respiratory, Renal, and
  // Musculoskeletal subjects belong to other courses in the program.
  courseFolderIds: CS101_FOLDER_IDS,
  references: [
    {
      id: "ref1",
      name: "Week 4 lecture slides.pdf",
      kind: "PDF",
      size: "4.2 MB",
      pages: 38,
    },
    {
      id: "ref2",
      name: "Cardiology reading pack.docx",
      kind: "DOCX",
      size: "1.1 MB",
      pages: 12,
    },
    {
      id: "ref3",
      name: "CS-101 midterm blueprint.pdf",
      kind: "PDF",
      size: "820 KB",
      pages: 6,
    },
    {
      id: "ref4",
      name: "Heart failure case bank.docx",
      kind: "DOCX",
      size: "2.4 MB",
      pages: 28,
    },
    {
      id: "ref5",
      name: "ECG interpretation drills.pdf",
      kind: "PDF",
      size: "3.1 MB",
      pages: 22,
    },
    {
      id: "ref6",
      name: "Pharmacology MCQ set.xlsx",
      kind: "XLSX",
      size: "540 KB",
    },
    {
      id: "ref7",
      name: "Neurology OSCE stems.docx",
      kind: "DOCX",
      size: "1.8 MB",
      pages: 15,
    },
    {
      id: "ref8",
      name: "Respiratory physio quiz.pdf",
      kind: "PDF",
      size: "1.2 MB",
      pages: 9,
    },
    {
      id: "ref9",
      name: "Musculoskeletal assessment items.docx",
      kind: "DOCX",
      size: "960 KB",
      pages: 11,
    },
    {
      id: "ref10",
      name: "Lab values rapid review.pdf",
      kind: "PDF",
      size: "2.0 MB",
      pages: 14,
    },
    {
      id: "ref11",
      name: "Ethics vignettes bank.docx",
      kind: "DOCX",
      size: "640 KB",
      pages: 8,
    },
    {
      id: "ref12",
      name: "Pediatric dosing scenarios.pdf",
      kind: "PDF",
      size: "1.5 MB",
      pages: 10,
    },
    {
      id: "ref13",
      name: "Infection control checklist.docx",
      kind: "DOCX",
      size: "410 KB",
      pages: 5,
    },
    {
      id: "ref14",
      name: "Renal acid-base items.pdf",
      kind: "PDF",
      size: "1.9 MB",
      pages: 16,
    },
    {
      id: "ref15",
      name: "Endocrine pathway matching.xlsx",
      kind: "XLSX",
      size: "380 KB",
    },
    {
      id: "ref16",
      name: "Clinical reasoning short answers.docx",
      kind: "DOCX",
      size: "1.3 MB",
      pages: 13,
    },
    {
      id: "ref17",
      name: "Charge On — CH1 charging systems.pdf",
      kind: "PDF",
      size: "2.7 MB",
      pages: 19,
    },
    {
      id: "ref18",
      name: "PRD Add question into assessment.docx",
      kind: "DOCX",
      size: "890 KB",
      pages: 7,
    },
    {
      id: "ref19",
      name: "Immunology hypersensitivity set.pdf",
      kind: "PDF",
      size: "1.6 MB",
      pages: 12,
    },
    {
      id: "ref20",
      name: "Microbiology organism bank.docx",
      kind: "DOCX",
      size: "2.1 MB",
      pages: 21,
    },
    {
      id: "ref21",
      name: "Geriatric polypharmacy cases.pdf",
      kind: "PDF",
      size: "1.4 MB",
      pages: 11,
    },
    {
      id: "ref22",
      name: "Wound care staging quiz.docx",
      kind: "DOCX",
      size: "720 KB",
      pages: 6,
    },
  ],
  blueprintGaps: [
    {
      topic: "Cardiology",
      need: "2 Hard MCQ",
      severity: "high",
      targetCount: 2,
      difficulty: "Hard",
      questionType: "Multiple Choice",
      bloom: "Analyze",
    },
    {
      topic: "Cardiology",
      need: "3 Medium MCQ",
      severity: "medium",
      targetCount: 3,
      difficulty: "Medium",
      questionType: "Multiple Choice",
      bloom: "Apply",
    },
    {
      topic: "Cardiology",
      need: "1 Hard Essay",
      severity: "medium",
      targetCount: 1,
      difficulty: "Hard",
      questionType: "Essay",
      bloom: "Evaluate",
    },
    {
      topic: "Neurology",
      need: "2 Medium MCQ",
      severity: "medium",
      targetCount: 2,
      difficulty: "Medium",
      questionType: "Multiple Choice",
      bloom: "Understand",
    },
    {
      topic: "Neurology",
      need: "1 Medium Essay",
      severity: "medium",
      targetCount: 1,
      difficulty: "Medium",
      questionType: "Essay",
      bloom: "Apply",
    },
    {
      topic: "Pharmacology",
      need: "1 Easy MCQ",
      severity: "medium",
      targetCount: 1,
      difficulty: "Easy",
      questionType: "Multiple Choice",
      bloom: "Remember",
    },
    {
      topic: "Pharmacology",
      need: "2 Medium short answers",
      severity: "medium",
      targetCount: 2,
      difficulty: "Medium",
      questionType: "Short Answer",
      bloom: "Analyze",
    },
  ],
  /**
   * Every paper `PAST_ASSESSMENT_LABELS` files questions under, listed newest
   * first: within one year the order here is what breaks the tie, and a final
   * comes after a midterm. Leaving one out stranded its questions in a row the
   * navigator could not name.
   */
  pastAssessments: PAST_ASSESSMENTS,
}

export function questionsForSource(
  source: Exclude<AddQuestionSource, "create">
): AssessmentBankQuestion[] {
  return ASSESSMENT_BANK_QUESTIONS.filter((q) => q.sourceKind === source)
}

export function parseAddQuestionSource(
  value: string | null | undefined
): AddQuestionSource {
  if (
    value === "past" ||
    value === "import" ||
    value === "create" ||
    value === "library"
  ) {
    return value
  }
  return "library"
}
